import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Edit, Trash2 } from 'lucide-react';
import { generateSerialColorMap } from '../utils/machineColors';
import { normalizeGreek } from '../utils/greekUtils';

const DataTable = ({ 
  data = [], 
  columns = [], 
  onEdit, 
  onDelete,
  actions,
  groupByKey = null,
  searchPlaceholder = "Αναζήτηση...",
  itemsPerPage = 10 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Generate color map for grouping
  const colorMap = useMemo(() => {
    if (!groupByKey) return {};
    return generateSerialColorMap(data, groupByKey);
  }, [data, groupByKey]);

  // Handle sort
  const handleSort = (columnKey) => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key: columnKey, direction: 'asc' };
    });
    setCurrentPage(1);
  };

  // Sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3" />;
    return <ArrowDown className="h-3 w-3" />;
  };

  // Filter data based on search
  const filteredData = data.filter(item => {
    const normSearch = normalizeGreek(searchTerm).toLowerCase();
    return columns.some(column => {
      const value = item[column.key];
      return normalizeGreek(value?.toString() || '').toLowerCase().includes(normSearch);
    });
  });

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = normalizeGreek(String(aVal)).localeCompare(normalizeGreek(String(bVal)), 'el', { numeric: true });
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const hasActions = onEdit || onDelete || actions;

  // Get row background color based on grouping
  const getRowColor = (item, index) => {
    if (groupByKey && item[groupByKey] && colorMap[item[groupByKey]]) {
      return colorMap[item[groupByKey]];
    }
    return index % 2 === 0 ? 'white' : '#f9fafb';
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10"
          />
        </div>
        <p className="text-sm text-gray-600">
          {sortedData.length} αποτελέσματα
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full min-w-0">
            <thead className="bg-[#004a99] text-white">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold cursor-pointer hover:bg-[#003a7a] select-none transition-colors whitespace-nowrap"
                    onClick={() => handleSort(column.key)}
                    data-testid={`sort-header-${column.key}`}
                  >
                    <div className="flex items-center gap-1">
                      <span>{column.label}</span>
                      {getSortIcon(column.key)}
                    </div>
                  </th>
                ))}
                {hasActions && (
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold">
                    Ενέργειες
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (hasActions ? 1 : 0)}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Δεν βρέθηκαν αποτελέσματα
                  </td>
                </tr>
              ) : (
                currentData.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{ backgroundColor: getRowColor(item, startIndex + index) }}
                    className="hover:brightness-95 transition-colors"
                  >
                    {columns.map((column) => (
                      <td key={column.key} className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 max-w-[120px] sm:max-w-none truncate">
                        {column.render ? column.render(item) : item[column.key]}
                      </td>
                    ))}
                    {hasActions && (
                      <td className="px-1 sm:px-4 py-2 sm:py-3 text-center">
                        {actions ? (
                          actions(item)
                        ) : (
                          <div className="flex items-center justify-center gap-1 sm:gap-2">
                            {onEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEdit(item)}
                                className="text-xs px-2 py-1 h-7 sm:h-8"
                              >
                                <span className="hidden sm:inline">Επεξεργασία</span>
                                <Edit className="h-3 w-3 sm:hidden" />
                              </Button>
                            )}
                            {onDelete && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => onDelete(item)}
                                className="text-xs px-2 py-1 h-7 sm:h-8"
                              >
                                <span className="hidden sm:inline">Διαγραφή</span>
                                <Trash2 className="h-3 w-3 sm:hidden" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Σελίδα {currentPage} από {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
